import {ApprovalResult} from '@financial/sbill/dist/approvals/types'
import {createAction, createAsyncThunk} from '@reduxjs/toolkit'

import {eq} from '@shared/helpers/lang'

import {historyChange, HistoryChangeAction, systemConfirm} from '@web/actions/application'
import {
    EntryType,
    FDS_AUTH_LEVEL,
    FDSAuthLevel,
    OrderEntry,
    OrderProcessState,
    OrderProcessStateSyncMap,
    OrderProcessStateType,
    OrderSheetPageSet,
    OrderSheetPageSetType,
    UserAuthStatus,
    UserAuthStatusType,
} from '@web/core/constants'
import {ARS, PASS_BIO_AUTH} from '@web/core/urls'
import {checkThreeDs} from '@web/helpers/3ds'
import {createApiAsyncThunk} from '@web/helpers/api'
import {getSessionKeyForFdsAuth} from '@web/helpers/fds'
import {sendTimestampToNelo} from '@web/helpers/nelo'
import {getQuery} from '@web/helpers/path'
import {createSocketProcessor, disconnectAllSocket as disconnectAllSocketFn} from '@web/helpers/socket'
import {LanguageType} from '@web/i18n'

import {getApis, OrderPathType} from '../apis'
import {OrderAuthentication, OrderProcessResult as OrderProcessResultByApi} from '../apis/interface'
import {
    CauseCode,
    ErrorMessage,
    FdsResultCase,
    FdsResultCaseType,
    FinishCode,
    ProcessingStep,
    ProcessingStepType,
} from '../constants'
import {
    Course,
    CourseType,
    FinishStatus,
    OrderProcessFail,
    OrderProcessResult,
    OrderRequest,
    PreparePayload,
} from '../interfaces'
import {orderProcessorSelector, orderProcessStepInfoSelector, urlInfoSelector} from '../selectors'
import {ThunkApi} from '../store'
import {getCourse, getCourseInfo, isCheckoutCourse, isPayNextCourse} from '../utils/course'
import {getARSUrl, getPassBioAuthUrl} from '../utils/fds'
import {
    getApiErrorFinished,
    getEmptyOrderRequestFinished,
    getNotFoundCourseFinished,
    getOrderProcessErrorFinished,
    getSuccessFinished,
} from '../utils/finished'
import {createBillingProcessor} from '../utils/sbill'

const api = getApis('/orderApi')

/** reject value */
type RejectedValue = {rejected: FinishStatus}

/** reject 판단 */
function isRejected<T extends object>(value: T | RejectedValue): value is RejectedValue {
    return 'rejected' in value
}

/** 주문형 주문서 path type 을 반환 합니다. */
function getOrderPath(course: CourseType): OrderPathType {
    const {isEasyBooking, isSeller, isFamily} = getCourseInfo(course)
    if (isEasyBooking) {
        return 'easyBooking'
    } else if (isSeller) {
        return 'seller'
    } else if (isFamily) {
        return 'familyPay'
    }
    return 'mall'
}

/** 주문형 주문서 path type 을 반환 합니다. */
function getOrderPathByPageSet(pageSet: Maybe<OrderSheetPageSetType>): OrderPathType {
    const eqPageSet = eq(pageSet)
    if (eqPageSet(OrderSheetPageSet.EASYBOOKING)) {
        return 'easyBooking'
    }
    if (eqPageSet(OrderSheetPageSet.SELLER)) {
        return 'seller'
    }
    if (eqPageSet(OrderSheetPageSet.FAMILY_PAY)) {
        return 'familyPay'
    }
    return 'mall'
}

function getPageSet(entry: Maybe<EntryType>) {
    const eqChannelType = eq(entry)
    if (eqChannelType(OrderEntry.CHECKOUT)) {
        return OrderSheetPageSet.MALL
    }
    if (
        eqChannelType(OrderEntry.EASY_BOOKING) ||
        eqChannelType(OrderEntry.LOCAL_PAY) ||
        eqChannelType(OrderEntry.NAVER_CAFE) ||
        eqChannelType(OrderEntry.NAVER_BLOG) ||
        eqChannelType(OrderEntry.FLEA_MARKET)
    ) {
        return OrderSheetPageSet.EASYBOOKING
    }
    if (eqChannelType(OrderEntry.SHOPN) || eqChannelType(OrderEntry.GIFTING) || eqChannelType(OrderEntry.BRANCH)) {
        return OrderSheetPageSet.SELLER
    }
    if (eqChannelType(OrderEntry.FAMILY_PAY)) {
        return OrderSheetPageSet.FAMILY_PAY
    }
    return OrderSheetPageSet.MALL
}

const CheckoutChannelGroup: OrderSheetPageSetType[] = [
    OrderSheetPageSet.MALL,
    OrderSheetPageSet.EASYBOOKING,
    OrderSheetPageSet.SELLER,
]

/** 주문 프로세스의 모든 소켓을 해제 합니다 */
export const disconnectAllSocket = createAction('orderProcessor/disconnectAllSocket', () => {
    return {
        payload: disconnectAllSocketFn(),
    }
})

/** 주문 프로세스를 중단시킵니다. */
export const abort = createAction<FinishStatus>('orderProcessor/abort')

/** 주문 프로세스를 종료합니다. */
export const finish = createAction('orderProcessor/finish', () => {
    return {
        payload: getSuccessFinished(),
    }
})

/** 주문 프로세스를 초기화 합니다. */
export const clear = createAction('orderProcessor/clear')

/** 주문 프로세스를 시작합니다. */
export const start = createAsyncThunk<
    {
        firstStep: ProcessingStepType
        steps: ProcessingStepType[]
        orderRequest: OrderRequest
    },
    OrderRequest,
    ThunkApi<FinishStatus>
>('orderProcessor/start', async (payload, {getState, rejectWithValue}) => {
    const state = getState()
    if (!orderProcessStepInfoSelector(state).isNotStarted) {
        // 현재 생성된 주문 프로세스와 비교하여 중복 생성이면, block 처리합니다.
        return rejectWithValue({
            code: 'PREVENT_OVERLAPPED_ORDER_PROCESS',
            message: '주문 프로세스가 이미 실행중입니다.',
        })
    }

    const orderRequest = payload

    const {steps, firstStep} = getCourse(orderRequest.course)
    if (!firstStep) {
        // TODO code 지정
        return rejectWithValue({
            code: 'UNKNOWN',
            message: '첫번째 step 이 없습니다',
        })
    }

    return {
        steps,
        firstStep,
        orderRequest,
    }
})

/** 주문을 검증합니다 */
export const prepare = createApiAsyncThunk<PreparePayload, OrderSheetPageSetType, ThunkApi<FinishStatus>>(
    'orderProcessor/prepare',
    async (_, {getState, rejectWithValue}) => {
        const state = getState()
        const {orderRequest, verificationKey, deviceLockType, orderNo} = orderProcessorSelector(state)

        if (!orderRequest) {
            return rejectWithValue(getEmptyOrderRequestFinished())
        }

        if (isCheckoutCourse(orderRequest)) {
            if (!orderRequest.requestData) {
                return rejectWithValue(getEmptyOrderRequestFinished())
            }
            const {orderSheetId} = orderRequest.essence
            const result = await api.fetchOrderPrepare({
                type: getOrderPath(orderRequest.course),
                requestData: orderRequest.requestData,
                verificationKey,
                orderSheetId,
                deviceLockType,
            })
            if (!result.orderProcessId) {
                return rejectWithValue({
                    code: FinishCode.PREPARE_FAIL,
                    message: 'orderProcessId 가 없습니다.',
                })
            }
            return result
        } else if (isPayNextCourse(orderRequest)) {
            if (!orderRequest.requestData) {
                return rejectWithValue(getEmptyOrderRequestFinished())
            }
            const result = await api.fetchOrderPrepareNext({
                type: getOrderPath(orderRequest.course),
                requestData: orderRequest.requestData,
                verificationKey,
                orderNo,
                deviceLockType,
            })
            if (!result.prepareFamilyPayId) {
                return rejectWithValue({
                    code: FinishCode.PREPARE_FAIL,
                    message: 'preparePayNextId 가 없습니다',
                })
            }
            return result
        }
        return rejectWithValue(getNotFoundCourseFinished())
    },
    {
        rejectWithUnknownApiError: (err: unknown) => getApiErrorFinished(FinishCode.PREPARE_FAIL, err),
        rejectWithUnknownError: (err: unknown) => getApiErrorFinished(FinishCode.PREPARE_FAIL, err),
    },
)

/** 주문서 서버에 검증을 요청합니다 */
export const processOrder = createApiAsyncThunk<OrderProcessResult, void, ThunkApi<FinishStatus>>(
    'orderProcessor/processOrder',
    async (_, {dispatch, getState, rejectWithValue}) => {
        const state = getState()
        const {
            orderRequest,
            prepare: {orderProcessId, orderSessionIoAccessUrl, orderSessionIoChannel},
        } = orderProcessorSelector(state)
        if (!orderRequest) {
            return rejectWithValue(getEmptyOrderRequestFinished())
        }
        const {steps} = getCourse(orderRequest.course)
        if (!steps.includes(ProcessingStep.PROCESSING)) {
            return rejectWithValue({
                code: CauseCode.SKIP,
                message: 'order 를 요청 하지 않습니다',
            })
        }

        if (isCheckoutCourse(orderRequest)) {
            const apiType = getOrderPath(orderRequest.course)
            const syncMap = OrderProcessStateSyncMap.filter(
                (orderProcessState): orderProcessState is OrderProcessStateType =>
                    orderProcessState !== OrderProcessState.ORDER_COMPLETED,
            )
            const result = await createSocketProcessor<OrderProcessResultByApi>({
                url: orderSessionIoAccessUrl,
                channel: orderSessionIoChannel,
                socketEvents: syncMap,
                endPollingWait: (orderProcessResult) => syncMap.includes(orderProcessResult.state),
                pollingPromise: () =>
                    api.fetchOrderProcess({
                        type: apiType,
                        orderProcessId,
                        entry: 'process',
                    }),
                noticeReadyPromise: () =>
                    api.beginOrderProcess({
                        type: apiType,
                        orderProcessId,
                    }),
                sendLogger: (message: string) => {
                    sendTimestampToNelo({
                        entry: orderRequest.entry,
                        pageSet: getPageSet(orderRequest.entry),
                        orderSheetId: orderRequest.essence.orderSheetId,
                        orderProcessId,
                        message,
                    })
                },
            })
            if (!result) {
                return rejectWithValue({
                    code: FinishCode.PROCESSING_FAILURE,
                    message: ErrorMessage.OrderApiFail,
                })
            }
            // ORDER_STARTED 경우에만, 결제요청 시작
            if (result.state !== OrderProcessState.ORDER_STARTED) {
                if (
                    (result.orderProcessValidationFailureIgnoredAsTooLong ||
                        result.isOrderProcessValidationFailureIgnoredAsTooLong) &&
                    !result.orderProcessValidationFailure
                ) {
                    try {
                        /** 주문 프로세스 유효성 검사 실패시, 너무 길어서 소켓으로 정보를 받을 수 없다면, api 로 데이터 반영 */
                        const errorResult = await api.fetchOrderProcess({
                            type: apiType,
                            orderProcessId,
                            entry: 'processTooLong',
                        })
                        return rejectWithValue(
                            getOrderProcessErrorFinished(FinishCode.PROCESSING_LONG_FAILURE, errorResult),
                        )
                    } catch (e) {
                        return rejectWithValue(getApiErrorFinished(FinishCode.PROCESSING_LONG_FAILURE, e))
                    }
                }
                return rejectWithValue(getOrderProcessErrorFinished(FinishCode.PROCESSING_LONG_FAILURE, result))
            }
            /**
             * process 중간에 하는 것이 맞나 확인 필요
             * https://oss.fin.navercorp.com/payment-fe/order-web/issues/4099#issuecomment-754297
             */
            if (result.needHopeDeliveryNotice) {
                const isConfirmed = await new Promise<boolean>((resolve) => {
                    dispatch(
                        systemConfirm({
                            type: 'CONFIRM',
                            message:
                                '나중에 결제/무통장입금 결제수단은 결제완료/입금시점에 따라\n배송 일자가 조정될 수 있으니, 판매자에게 반드시 배송 일자를\n다시 확인하시기 바랍니다.',
                            callback: (confirmed: boolean) => {
                                resolve(confirmed)
                            },
                        }),
                    )
                })
                if (!isConfirmed) {
                    return rejectWithValue(
                        getOrderProcessErrorFinished(FinishCode.NEED_HOPE_DELIVERY_NOTICE_FAIL, result),
                    )
                }
            }

            return {
                orderProcessId: result.orderProcessId,
                orderSheetId: result.orderSheetId,
                status: result.state,
                orderNo: result.orderNo,
                orderAuthentication: result.orderAuthentication,
            }
        }
        return rejectWithValue(getNotFoundCourseFinished())
    },
    {
        rejectWithUnknownApiError: (err: unknown) => getApiErrorFinished(FinishCode.PROCESSING_FAILURE, err),
        rejectWithUnknownError: (err: unknown) => getApiErrorFinished(FinishCode.PROCESSING_FAILURE, err),
    },
)

/** 일반 결제수단 (카드, 모바일) 을 검증 합니다. */
export const processVan = createApiAsyncThunk<ApprovalResult, void, ThunkApi<FinishStatus>>(
    'orderProcessor/processVan',
    async (_, {getState, rejectWithValue}) => {
        const state = getState()
        const {orderRequest, orderProcessResult} = orderProcessorSelector(state)

        if (!(orderRequest && orderRequest.shopBill)) {
            return rejectWithValue({
                code: CauseCode.SKIP,
                message: 'VAN 체크를 하지 않습니다',
            })
        }

        if (!orderProcessResult?.orderNo) {
            return rejectWithValue({
                code: 'VAN_APPROVAL_FAIL',
                message: 'orderNo 가 없습니다',
            })
        }

        const {reservePayload: reservationPayload, approvePayload: payload} = orderRequest.shopBill

        const approvalResult = await createBillingProcessor(reservationPayload, {
            ...payload,
            packageOrderId: orderProcessResult?.orderNo,
        })

        if (!approvalResult.success) {
            return rejectWithValue({
                code: 'VAN_APPROVAL_FAIL',
                message: approvalResult.message,
            })
        }
        return approvalResult
    },
    {
        rejectWithUnknownApiError: () => ({
            code: 'VAN_APPROVAL_FAIL',
            message: 'BILLING ERROR',
        }),
        rejectWithUnknownError: () => ({
            code: 'VAN_APPROVAL_FAIL',
            message: 'BILLING ERROR',
        }),
    },
)

/** FDS 인증츨 처리 합니다. */
type FDSPayload = {url: string; sessionKey?: Maybe<string>}

/** FDS 인증츨 처리 합니다. */
export const processFDS = createApiAsyncThunk<FDSPayload, void, ThunkApi<FinishStatus>>(
    'orderProcessor/processFDS',
    async (_, {getState, rejectWithValue}) => {
        const state = getState()
        const {
            orderRequest,
            fds: {authentication, actionType},
        } = orderProcessorSelector(state)
        const {applyUrl, orderSheetUrl} = urlInfoSelector(state)
        const {fdsAuthBridgeUrl = ''} = orderRequest?.url ?? {}

        const sessionKey = authentication?.sessionKey ?? ''
        const fdsUuid = authentication?.fdsUuid ?? ''

        const passBioAuthUrl = PASS_BIO_AUTH

        const fdsAuthLevelMap: Record<FDSAuthLevel, FDSPayload | RejectedValue> = {
            // FDS 인증 불필요
            [FDS_AUTH_LEVEL.SKIP_AUTH]: {url: ''},
            // 지문인증
            [FDS_AUTH_LEVEL.PASSWORD]: {
                url: getPassBioAuthUrl({
                    actionType,
                    applyUrl,
                    closeable: actionType === 'REPLACE',
                    fdsAuthBridgeUrl,
                    orderSheetUrl,
                    passBioAuthUrl,
                    sessionKey,
                }),
            },
            // ARS
            [FDS_AUTH_LEVEL.ARS]: {
                url: getARSUrl({
                    arsUrl: ARS,
                    fdsAuthBridgeUrl,
                    actionType,
                    fdsUuid,
                    applyUrl,
                    orderSheetUrl,
                }),
            },
            // 3DS 인증. 브라우저에서 진행
            [FDS_AUTH_LEVEL.THREE_DS]: {url: ''},
            // 결제 한도 초과
            [FDS_AUTH_LEVEL.SELF_BLOCK]: {
                rejected: {
                    code: FinishCode.FDS_PAYMENT_SECURITY,
                    message: '설정하신 결제 한도를 초과하여 결제가 불가능 합니다',
                },
            },
            // FDS 차단
            [FDS_AUTH_LEVEL.BLOCK]: {
                rejected: {
                    code: FinishCode.FDS_BLOCKED,
                    message: 'FDS 차단 되었습니다',
                },
            },
        }
        const result: FDSPayload | RejectedValue = authentication?.level
            ? fdsAuthLevelMap[authentication.level]
            : {
                  rejected: {
                      code: FinishCode.UNKNOWN,
                      message: '알수 없는 FDS CODE',
                  },
              }

        if (isRejected(result)) {
            return rejectWithValue(result.rejected)
        }

        /** 3ds 인증 단계 수행 */
        if (authentication?.level === FDS_AUTH_LEVEL.THREE_DS) {
            const {cardId, cardCompanyCode} = orderRequest?.requestData?.payment.cardEasyPayment ?? {}

            try {
                const {sessionKey: threeDSSessionKey} = await checkThreeDs({
                    cardInfo: {cardId, cardCompanyCode},
                    fdsInfo: {fdsUuid},
                })

                return {
                    ...result,
                    sessionKey: threeDSSessionKey,
                }
            } catch (error) {
                /** @see https://oss.fin.navercorp.com/Product-Planning/Order_QA/issues/10820#issuecomment-1883944 */
                return rejectWithValue({
                    code: FinishCode.THREE_DS_FAILURE,
                    message: `3ds 인증이 실패했습니다. - ${JSON.stringify(error)}`,
                })
            }
        }

        return result
    },
)

/** FDS 인증 시작 */
export const startFDS = createAsyncThunk<void, void, ThunkApi<FinishStatus>>('orderProcessor/startFDS', () => {
    /* empty */
})

/** FDS 인증 결과를 처리 합니다. */
export const finishFDS = createAsyncThunk<boolean, FdsResultCaseType, ThunkApi<FinishStatus>>(
    'orderProcessor/finishFDS',
    (payload, {rejectWithValue}) => {
        if (payload === FdsResultCase.CANCEL) {
            return rejectWithValue({
                code: 'FDS_CANCEL',
                message: '인증이 취소되었습니다.',
            })
        } else if (payload === FdsResultCase.PASSWORD_CHANGE) {
            return rejectWithValue({
                code: 'FDS_PASSWORD_CHANGE',
                message: '안전한 결제를 위해 변경된 비밀번호로 다시 한번 결제해주시기 바랍니다.',
            })
        }
        return payload === FdsResultCase.SUCCESS
    },
)

const ChannelGroupMap: Record<OrderSheetPageSetType, CourseType> = {
    [OrderSheetPageSet.MALL]: Course.MALL,
    [OrderSheetPageSet.EASYBOOKING]: Course.SELLER,
    [OrderSheetPageSet.SELLER]: Course.SELLER,
    [OrderSheetPageSet.FAMILY_PAY]: Course.FAMILY,
    // todo - feat/19429: 추후 따로 코스를 정의해야 할지 검토 필요. 임시로 FAMILY 코스 적용.
    [OrderSheetPageSet.CLAIM]: Course.FAMILY,
}

/** sessionKey를 orderAuthentication 객체에 업데이트 */
const overrideAuthData = ({
    orderAuthentication,
    arsSessionKey,
    threeDsSessionKey,
}: {
    orderAuthentication: OrderAuthentication
    arsSessionKey: Maybe<string>
    threeDsSessionKey: Maybe<string>
}): OrderAuthentication => {
    // sessionKey가 없으면 덮어씌울 필요 없음.
    if (!arsSessionKey && !threeDsSessionKey) {
        return orderAuthentication
    }

    const sessionKey = arsSessionKey || threeDsSessionKey
    const authenticationLevel = arsSessionKey ? FDS_AUTH_LEVEL.ARS : FDS_AUTH_LEVEL.THREE_DS

    if (orderAuthentication) {
        // orderAuthentication 필드에 값이 있으면 sessionKey만 업데이트
        return {
            ...orderAuthentication,
            sessionKey,
        }
    } else {
        // orderAuthentication이 null이면 authenticationLevel까지 업데이트
        return {
            sessionKey,
            authenticationLevel,
            fdsUuid: null,
            fdsUnavailablePaymentType: null,
        }
    }
}

/** 결제 승인 요청 */
export const startApply = createApiAsyncThunk<
    OrderProcessResultByApi & {
        applyOrderRequest: Maybe<{
            approvalSeq?: string
            virtualAccountNo?: string
            /** 입금 기한 expirationDepositDate */
            payExpirationDate?: string
        }>
        channel: EntryType
        channelGroup: OrderSheetPageSetType
        processSteps: ProcessingStepType[]
    },
    {
        seqID?: string
        orderProcessNo: string
        channel: Maybe<EntryType>
        channelGroup: Maybe<OrderSheetPageSetType>
    },
    ThunkApi<FinishStatus>
>('orderProcessor/startApply', async ({orderProcessNo, seqID, channel, channelGroup}, {rejectWithValue}) => {
    if (!channel || !channelGroup) {
        return rejectWithValue(getEmptyOrderRequestFinished())
    }

    const applyOrderRequest = (function (): Maybe<{
        approvalSeq?: string
        virtualAccountNo?: string
        payExpirationDate?: string
    }> {
        try {
            const {seq, van, ped} = getQuery<{
                seq: string
                van: string
                ped: string
            }>()
            if (seq || van || ped) {
                return {
                    approvalSeq: seq,
                    virtualAccountNo: van,
                    payExpirationDate: ped,
                }
            }
            return null
        } catch {
            return null
        }
    })()

    const {arsSessionKey = null, tds = null} = getQuery<{
        arsSessionKey?: string
        tds?: string
    }>()

    const {steps: processSteps} = getCourse(ChannelGroupMap[channelGroup])

    if (channelGroup === OrderSheetPageSet.FAMILY_PAY) {
        return {
            applySessionIoAccessUrl: null,
            applySessionIoChannel: null,
            orderSessionIoAccessUrl: null,
            orderSessionIoChannel: null,
            orderAuthentication: overrideAuthData({
                orderAuthentication: null,
                arsSessionKey,
                threeDsSessionKey: tds,
            }),
            startedDateTime: '',
            canceledDateTime: '',
            completedDateTime: '',
            lastStatusDateTime: '',
            state: 'ORDER_STARTED',
            version: 0,
            remoteAdditionalDeliveryFeeFailed: null,
            remoteAdditionalDeliveryFeeFailureMessage: null,
            canceled: false,
            applyOrderProcessFailure: null,
            applyOrderProcessValidationFailure: null,
            orderProcessFailure: null,
            orderProcessValidationFailure: null,
            orderProcessValidationFailureIgnoredAsTooLong: null,
            orderNo: seqID ?? '',
            orderSheetId: seqID ?? '',
            orderProcessId: orderProcessNo,
            channel: 'FAMILY_PAY',
            channelGroup,
            applyOrderRequest,
            processSteps,
            foreignerOrder: null,
        }
    }

    const result = await api.fetchOrderProcess({
        type: getOrderPathByPageSet(channelGroup),
        orderProcessId: orderProcessNo,
        entry: 'check',
    })

    if (
        OrderProcessStateSyncMap.filter(
            (status): status is OrderProcessStateType =>
                !(status === OrderProcessState.ORDER_STARTED || status === OrderProcessState.ORDER_COMPLETED),
        ).includes(result.state)
    ) {
        return rejectWithValue(getEmptyOrderRequestFinished())
    }

    return {
        ...result,
        orderAuthentication: overrideAuthData({
            orderAuthentication: result.orderAuthentication,
            arsSessionKey,
            threeDsSessionKey: tds,
        }),
        channel,
        channelGroup,
        applyOrderRequest,
        processSteps,
    }
    // 만약 사용자가 강제로 해당 페이지에 진입 하려고 한다면 튕겨내야함
    // return rejectWithValue(getEmptyOrderRequestFinished())
})

/** 결제 승인을 요청합니다. */
export const apply = createApiAsyncThunk<void, void, ThunkApi<FinishStatus>>(
    'orderProcessor/apply',
    async (_, {dispatch, getState, rejectWithValue}) => {
        const state = getState()
        const {
            orderSheetId,
            orderNo,
            channelGroup,
            prepare: {orderProcessId, applySessionIoAccessUrl, applySessionIoChannel},
            applyOrderRequest: {approvalSeq},
            orderProcessResult: processResult,
        } = orderProcessorSelector(state)

        const {arsSessionKey, threeDsSessionKey} =
            getSessionKeyForFdsAuth({
                sessionKey: processResult?.orderAuthentication?.sessionKey,
                level: processResult?.orderAuthentication?.authenticationLevel,
            }) ?? {}

        if (channelGroup && CheckoutChannelGroup.includes(channelGroup)) {
            const apiType = getOrderPathByPageSet(channelGroup)
            const syncMap = OrderProcessStateSyncMap.filter(
                (orderProcessState): orderProcessState is OrderProcessStateType =>
                    orderProcessState !== OrderProcessState.ORDER_STARTED,
            )

            const isAlreadyApply = processResult ? processResult.status !== OrderProcessState.ORDER_STARTED : false

            const result = await createSocketProcessor<OrderProcessResultByApi>({
                url: applySessionIoAccessUrl,
                channel: applySessionIoChannel,
                // 승인 요청 이후 새고로침하면, 결제 요청을 폴링으로 대기
                type: isAlreadyApply ? 'polling' : 'socket',
                socketEvents: syncMap,
                endPollingWait: (orderProcessResult) => syncMap.includes(orderProcessResult.state),
                pollingPromise: () =>
                    api.fetchOrderProcess({
                        type: apiType,
                        orderProcessId,
                        entry: 'apply',
                    }),
                noticeReadyPromise: () =>
                    new Promise((resolve, reject) => {
                        // 승인 요청후 새로고침을 통해 중복 결제 요청이 들어가지 않도록 방어
                        if (!isAlreadyApply) {
                            api.applyOrder({
                                type: apiType,
                                orderProcessId,
                                applyOrderRequest: {
                                    approvalSeq,
                                    // fds ars 인증의 경우 ars 인증페이지에서 발급받은 세션키를 전달해 주어야 be에서 검증이 가능함.
                                    ...(arsSessionKey ? {arsSessionKey} : {}),
                                    // 3ds 인증의 경우 3ds 인증과정에서 카드BE가 발급한 세션키를 전달해 주어야 be에서 검증이 가능함.
                                    ...(threeDsSessionKey ? {threeDomainSecureSessionKey: threeDsSessionKey} : {}),
                                },
                            })
                                .then(() => {
                                    dispatch(
                                        historyChange({
                                            type: HistoryChangeAction.REPLACE_STATE,
                                            state: {isAlreadyApply: true},
                                        }),
                                    )
                                    resolve()
                                })
                                .catch((e) => {
                                    reject(e)
                                })
                        } else {
                            resolve()
                        }
                    }),
                sendLogger: (message: string) => {
                    sendTimestampToNelo({
                        entry: null,
                        pageSet: channelGroup,
                        orderNo,
                        orderSheetId,
                        orderProcessId,
                        message,
                    })
                },
            })
            if (!result) {
                return rejectWithValue({
                    code: FinishCode.APPLY_FAILURE,
                    message: ErrorMessage.OrderApiFail,
                })
            }
            // ORDER_COMPLETED 경우에만, 결제승인
            if (!result.state || result.state !== OrderProcessState.ORDER_COMPLETED) {
                return rejectWithValue(getOrderProcessErrorFinished(FinishCode.APPLY_FAILURE, result))
            }
            // 주문완료
            return
        } else if (channelGroup === OrderSheetPageSet.FAMILY_PAY) {
            const apiType = getOrderPathByPageSet(channelGroup)
            await api.applyOrderNext({
                type: apiType,
                orderNo,
                prepareId: orderProcessId,
                // fds ars 인증의 경우 ars 인증페이지에서 발급받은 세션키를 전달해 주어야 be에서 검증이 가능함.
                ...(arsSessionKey ? {arsSessionKey} : {}),
            })
            return
        }
        // return rejectWithValue(getApiErrorFinished(FinishCode.APPLY_FAILURE, e))
        return rejectWithValue(getNotFoundCourseFinished())
    },
    {
        rejectWithUnknownApiError: (err) => getApiErrorFinished(FinishCode.APPLY_FAILURE, err),
        rejectWithUnknownError: (err) => getApiErrorFinished(FinishCode.APPLY_FAILURE, err),
    },
)

/** 결제 승인 실패 사유 반환 */
export const getApplyFailure = createAsyncThunk<
    OrderProcessFail & {
        orderNo: Maybe<string>
        orderSheetId: Maybe<string>
        language: Maybe<LanguageType>
        userAuthStatus: UserAuthStatusType
    },
    {
        apiType?: OrderPathType
        orderProcessNo: string
    },
    ThunkApi<FinishStatus>
>('orderProcessor/getApplyFailure', async ({orderProcessNo, apiType = 'mall'}) => {
    const data = await api.fetchOrderProcess({
        type: apiType,
        orderProcessId: orderProcessNo,
        entry: 'failure',
    })

    const {
        applyOrderProcessFailure,
        applyOrderProcessValidationFailure,
        orderProcessFailure,
        orderProcessValidationFailure,
        orderNo,
        orderSheetId,
        foreignerOrder,
        userAuthStatus,
    } = data

    return {
        orderNo,
        status: data.state,
        failure:
            applyOrderProcessValidationFailure ||
            applyOrderProcessFailure ||
            orderProcessValidationFailure ||
            orderProcessFailure,
        orderSheetId,
        language: foreignerOrder?.languageCode ?? 'ko',
        userAuthStatus: userAuthStatus ?? UserAuthStatus.CERTIFICATE,
    }
})

export const setAutoPay = createAction<boolean>('orderProcesser/setAutoPay')
